import { useState } from "react";
import { Archive, Lock, PenSquare, RotateCcw } from "lucide-react";
import ConfirmModal from "./ConfirmModal";
import { InlineIcon } from "./Icon";
import UserProfileButton from "./UserProfileButton";

function relativeDays(dateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "dzisiaj";
  const abs = Math.abs(diff);
  const unit = abs === 1 ? "dzień" : "dni";
  if (diff > 0) return `za ${abs} ${unit}`;
  return `${abs} ${unit} temu`;
}

export default function EventsScreen({
  auth,
  userTier,
  userLimits,
  events,
  loading,
  error,
  onRefresh,
  onCreateEvent,
  onUpdateEvent,
  onSelect,
  onLogout,
  onArchiveEvent,
  onRestoreEvent,
  onProfileClick,
}) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editEvent, setEditEvent] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [eventsFilter, setEventsFilter] = useState("active");
  const [isRefreshingEvents, setIsRefreshingEvents] = useState(false);

  const eventsAtLimit = userLimits?.events?.at_limit === true;

  const [createForm, setCreateForm] = useState({
    name: "",
    location: "",
    start_date: "",
    end_date: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    location: "",
    start_date: "",
    end_date: "",
  });

  const filteredEvents = events.filter((event) => {
    if (eventsFilter === "active") return event.active;
    if (eventsFilter === "archived") return !event.active;
    return true;
  });

  const eventsCounts = {
    active: events.filter((event) => event.active).length,
    archived: events.filter((event) => !event.active).length,
    all: events.length,
  };

  const handleRefreshEvents = async () => {
    if (loading || isRefreshingEvents) return;
    setIsRefreshingEvents(true);
    try {
      await Promise.all([
        Promise.resolve(onRefresh()),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } finally {
      setIsRefreshingEvents(false);
    }
  };

  const submitCreateEvent = async (e) => {
    e.preventDefault();
    setCreateError("");

    if (!createForm.name.trim()) {
      setCreateError("Nazwa wydarzenia jest wymagana.");
      return;
    }

    setCreateLoading(true);

    const payload = {
      name: createForm.name.trim(),
      location: createForm.location.trim() || null,
      start_date: createForm.start_date || null,
      end_date: createForm.end_date || null,
      active: true,
    };

    const result = await onCreateEvent(payload);

    setCreateLoading(false);

    if (!result.ok) {
      // Custom error mapping for date mismatch
      if (
        result.error.includes("Start date must be before end date")
      ) {
        setCreateError("Data rozpoczęcia musi być wcześniejsza niż data zakończenia.");
        return;
      }
      setCreateError(result.error || "Nie udało się utworzyć wydarzenia.");
      return;
    }

    setCreateForm({ name: "", location: "", start_date: "", end_date: "" });
    setShowCreateForm(false);
  };

  const openEditEvent = (event) => {
    setEditError("");
    setEditEvent(event);
    setEditForm({
      name: event.name || "",
      location: event.location || "",
      start_date: event.start_date || "",
      end_date: event.end_date || "",
    });
  };

  const submitEditEvent = async (e) => {
    e.preventDefault();
    setEditError("");

    if (!editForm.name.trim()) {
      setEditError("Nazwa wydarzenia jest wymagana.");
      return;
    }

    if (!editEvent?.id) {
      setEditError("Nie znaleziono wydarzenia do edycji.");
      return;
    }

    setEditLoading(true);
    const result = await onUpdateEvent(editEvent.id, {
      name: editForm.name.trim(),
      location: editForm.location.trim() || null,
      start_date: editForm.start_date || null,
      end_date: editForm.end_date || null,
    });
    setEditLoading(false);

    if (!result.ok) {
      setEditError(result.error || "Nie udało się zapisać zmian.");
      return;
    }

    setEditEvent(null);
  };

  return (
    <div
      style={{
        fontFamily: "var(--font-body)",
        background: "var(--color-bg-app)",
        minHeight: "100dvh",
        maxWidth: 480,
        width: "calc(100% - 16px)",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          background: "var(--color-bg-card)",
          borderBottom: "1px solid var(--color-border)",
          padding: "20px",
          paddingTop: "calc(20px + env(safe-area-inset-top))",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <h1
            style={{
              margin: "2px 0 0",
              fontFamily: "var(--font-display)",
              fontSize: "var(--text-h2)",
              color: "var(--color-text-primary)",
              fontWeight: "var(--font-semibold)",
            }}
          >
            Twoje wydarzenia
          </h1>
        </div>
        <UserProfileButton auth={auth} userTier={userTier} onProfileClick={onProfileClick} />
      </div>

      <div style={{ padding: 16 }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <button
            onClick={() => {
              if (eventsAtLimit) return;
              setShowCreateForm(true);
              setCreateError("");
            }}
            disabled={eventsAtLimit}
            style={{
              flex: 1,
              border: eventsAtLimit ? "1px solid var(--color-border)" : "none",
              background: eventsAtLimit ? "var(--color-bg-app)" : "var(--color-primary)",
              color: eventsAtLimit ? "var(--color-text-muted)" : "var(--color-bg-card)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontWeight: "var(--font-medium)",
              fontSize: "var(--text-body)",
              cursor: eventsAtLimit ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {eventsAtLimit && <InlineIcon Icon={Lock} style={{ marginRight: 2 }} />}
            {eventsAtLimit ? "Limit osiągnięty" : "+ Nowe wydarzenie"}
          </button>
          <button
            onClick={handleRefreshEvents}
            disabled={loading || isRefreshingEvents}
            style={{
              flex: 1,
              border: "1px solid var(--color-primary-border)",
              background: "var(--color-primary)",
              color: "var(--color-bg-card)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              fontWeight: "var(--font-medium)",
              fontSize: "var(--text-body)",
              cursor: loading ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              fontFamily: "inherit",
              opacity: loading || isRefreshingEvents ? 0.85 : 1,
            }}
          >
            <span className={loading || isRefreshingEvents ? "spin" : ""}>
              <InlineIcon Icon={RotateCcw} />
            </span>
            <span>{loading || isRefreshingEvents ? "Ładowanie..." : "Odśwież"}</span>
          </button>
        </div>

        {eventsAtLimit && (
          <div
            style={{
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              padding: "10px 12px",
              marginBottom: 12,
              fontSize: "var(--text-xs)",
              color: "var(--color-text-muted)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <InlineIcon Icon={Lock} />
            Osiągnięto limit {userLimits.events.max} {userLimits.events.max === 1 ? "wydarzenia" : "wydarzeń"} dla Twojego planu. Przejdź na wyższy plan, aby dodać więcej.
          </div>
        )}



        {error && (
          <div
            style={{
              background: "var(--color-danger-bg)",
              border: "1px solid var(--color-danger-bg)",
              color: "var(--color-danger)",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 12,
            overflowX: "auto",
          }}
        >
          {[
            { key: "active", label: "Aktywne" },
            { key: "archived", label: "Archiwalne" },
            { key: "all", label: "Wszystkie" },
          ].map((option) => (
            <button
              key={option.key}
              onClick={() => setEventsFilter(option.key)}
              style={{
                border: `1px solid ${
                  eventsFilter === option.key ? "var(--color-primary-border)" : "var(--color-border)"
                }`,
                background: eventsFilter === option.key ? "var(--color-primary-light)" : "var(--color-bg-card)",
                color: eventsFilter === option.key ? "var(--color-primary)" : "var(--color-text-muted)",
                borderRadius: "var(--radius-full)",
                padding: "6px 12px",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-medium)",
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontFamily: "inherit",
              }}
            >
              {option.label} ({eventsCounts[option.key]})
            </button>
          ))}
        </div>

        {!loading && events.length === 0 && (
          <div
            style={{
              textAlign: "center",
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-lg)",
              padding: "28px 18px",
              color: "var(--color-text-muted)",
            }}
          >
            Brak wydarzeń dla tego konta.
          </div>
        )}

        {!loading && events.length > 0 && filteredEvents.length === 0 && (
          <div
            style={{
              textAlign: "center",
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-lg)",
              padding: "22px 18px",
              color: "var(--color-text-muted)",
              marginBottom: 10,
            }}
          >
            Brak wydarzeń w tym filtrze.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              style={{
                border: "1px solid var(--color-border)",
                background: "var(--color-bg-card)",
                borderRadius: "var(--radius-lg)",
                overflow: "hidden",
              }}
            >
              <div style={{ padding: 14 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <button
                    onClick={() => onSelect(event)}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "block",
                      fontFamily: "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 700,
                        fontStyle: "normal",
                        fontSize: "14px",
                        lineHeight: "140%",
                        letterSpacing: "0",
                        color: "var(--color-text-primary)",
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          backgroundColor: event.active ? "var(--color-success)" : "var(--color-danger)",
                          flexShrink: 0,
                        }}
                      />
                      {event.name}
                    </div>
                    <div
                      style={{
                        fontFamily: "Roboto, sans-serif",
                        fontWeight: 400,
                        fontStyle: "normal",
                        fontSize: "12px",
                        lineHeight: "140%",
                        letterSpacing: "0",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      {event.location || "Brak lokalizacji"}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 4,
                      }}
                    >
                      {event.start_date ? (
                        <div
                          style={{
                            fontFamily: "Roboto, sans-serif",
                            fontWeight: 400,
                            fontStyle: "normal",
                            fontSize: "12px",
                            lineHeight: "140%",
                            letterSpacing: "0",
                            color: "var(--color-text-muted)",
                          }}
                        >
                          Start: {new Date(event.start_date).toLocaleDateString("pl-PL")}{" "}
                          <span style={{ color: "var(--color-text-muted)", opacity: 0.75 }}>({relativeDays(event.start_date)})</span>
                        </div>
                      ) : (
                        <div />
                      )}
                    </div>
                  </button>

                  <button
                    onClick={() => openEditEvent(event)}
                    aria-label="Edytuj wydarzenie"
                    style={{
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-card)",
                      color: "var(--color-text-muted)",
                      borderRadius: "var(--radius-md)",
                      width: 30,
                      height: 30,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                    title="Edytuj wydarzenie"
                  >
                    <InlineIcon Icon={PenSquare} />
                  </button>
                </div>
              </div>
              <button
                onClick={() =>
                  setConfirmAction({
                    type: event.active ? "archive" : "restore",
                    event,
                  })
                }
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  borderTop: "1px solid var(--color-border)",
                  padding: "8px 14px",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--font-medium)",
                  color: event.active ? "var(--color-text-muted)" : "var(--color-success)",
                  cursor: "pointer",
                  textAlign: "right",
                  fontFamily: "inherit",
                }}
              >
                {event.active ? (
                  <>
                    <InlineIcon Icon={Archive} style={{ marginRight: 4 }} />
                    Archiwizuj wydarzenie
                  </>
                ) : (
                  <>
                    <InlineIcon Icon={RotateCcw} style={{ marginRight: 4 }} />
                    Przywróć wydarzenie
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {showCreateForm && (
          <div
            onClick={() => !createLoading && setShowCreateForm(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              boxSizing: "border-box",
            }}
          >
            <form
              onSubmit={submitCreateEvent}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 420,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-lg)",
                padding: 14,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-h3)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--color-text-primary)",
                  marginBottom: 12,
                }}
              >
                Utwórz wydarzenie
              </div>

              {createError && (
                <div
                  style={{
                    background: "var(--color-danger-bg)",
                    border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
                    color: "var(--color-danger)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    marginBottom: 12,
                    fontSize: "var(--text-sm)",
                  }}
                >
                  {createError}
                </div>
              )}

              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Nazwa wydarzenia
                </div>
                <input
                  value={createForm.name}
                  onChange={(e) =>
                    setCreateForm((form) => ({ ...form, name: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Lokalizacja
                </div>
                <input
                  value={createForm.location}
                  onChange={(e) =>
                    setCreateForm((form) => ({ ...form, location: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <label style={{ display: "block", flex: 1 }}>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                    Start
                  </div>
                  <input
                    type="date"
                    value={createForm.start_date}
                    onChange={(e) =>
                      setCreateForm((form) => ({
                        ...form,
                        start_date: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      padding: "10px 12px",
                      fontSize: "var(--text-body)",
                      boxSizing: "border-box",
                    }}
                  />
                </label>

                <label style={{ display: "block", flex: 1 }}>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                    Koniec
                  </div>
                  <input
                    type="date"
                    value={createForm.end_date}
                    onChange={(e) =>
                      setCreateForm((form) => ({
                        ...form,
                        end_date: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      padding: "10px 12px",
                      fontSize: "var(--text-body)",
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  disabled={createLoading}
                  style={{
                    flex: 1,
                    border: "1px solid var(--btn-secondary-border)",
                    background: "var(--color-bg-card)",
                    color: "var(--btn-secondary-text)",
                    borderRadius: "var(--radius-md)",
                    padding: "11px 12px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-body)",
                    cursor: createLoading ? "default" : "pointer",
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "var(--color-primary)",
                    color: "var(--color-bg-card)",
                    borderRadius: "var(--radius-md)",
                    padding: "11px 12px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-body)",
                    cursor: createLoading ? "default" : "pointer",
                    opacity: createLoading ? 0.7 : 1,
                  }}
                >
                  {createLoading ? "Tworzenie..." : "Utwórz wydarzenie"}
                </button>
              </div>
            </form>
          </div>
        )}

        {editEvent && (
          <div
            onClick={() => !editLoading && setEditEvent(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
              boxSizing: "border-box",
            }}
          >
            <form
              onSubmit={submitEditEvent}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 420,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-lg)",
                padding: 14,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--text-h3)",
                  fontWeight: "var(--font-semibold)",
                  color: "var(--color-text-primary)",
                  marginBottom: 12,
                }}
              >
                Edytuj wydarzenie
              </div>

              {editError && (
                <div
                  style={{
                    background: "var(--color-danger-bg)",
                    border: "1px solid color-mix(in srgb, var(--color-danger) 28%, white)",
                    color: "var(--color-danger)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    marginBottom: 12,
                    fontSize: "var(--text-sm)",
                  }}
                >
                  {editError}
                </div>
              )}

              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Nazwa wydarzenia
                </div>
                <input
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm((form) => ({ ...form, name: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <label style={{ display: "block", marginBottom: 10 }}>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                  Lokalizacja
                </div>
                <input
                  value={editForm.location}
                  onChange={(e) =>
                    setEditForm((form) => ({ ...form, location: e.target.value }))
                  }
                  style={{
                    width: "100%",
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "10px 12px",
                    fontSize: "var(--text-body)",
                    boxSizing: "border-box",
                  }}
                />
              </label>

              <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                <label style={{ display: "block", flex: 1 }}>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                    Start
                  </div>
                  <input
                    type="date"
                    value={editForm.start_date}
                    onChange={(e) =>
                      setEditForm((form) => ({
                        ...form,
                        start_date: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      padding: "10px 12px",
                      fontSize: "var(--text-body)",
                      boxSizing: "border-box",
                    }}
                  />
                </label>

                <label style={{ display: "block", flex: 1 }}>
                  <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 6 }}>
                    Koniec
                  </div>
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) =>
                      setEditForm((form) => ({
                        ...form,
                        end_date: e.target.value,
                      }))
                    }
                    style={{
                      width: "100%",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      padding: "10px 12px",
                      fontSize: "var(--text-body)",
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setEditEvent(null)}
                  disabled={editLoading}
                  style={{
                    flex: 1,
                    border: "1px solid var(--btn-secondary-border)",
                    background: "var(--color-bg-card)",
                    color: "var(--btn-secondary-text)",
                    borderRadius: "var(--radius-md)",
                    padding: "11px 12px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-body)",
                    cursor: editLoading ? "default" : "pointer",
                  }}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  style={{
                    flex: 1,
                    border: "none",
                    background: "var(--color-primary)",
                    color: "var(--color-bg-card)",
                    borderRadius: "var(--radius-md)",
                    padding: "11px 12px",
                    fontWeight: "var(--font-medium)",
                    fontSize: "var(--text-body)",
                    cursor: editLoading ? "default" : "pointer",
                    opacity: editLoading ? 0.7 : 1,
                  }}
                >
                  {editLoading ? "Zapisywanie..." : "Zapisz zmiany"}
                </button>
              </div>
            </form>
          </div>
        )}

        {confirmAction && (
          <ConfirmModal
            eventName={confirmAction.event.name}
            actionType={confirmAction.type}
            onConfirm={async () => {
              if (confirmAction.type === "archive") {
                await onArchiveEvent(confirmAction.event);
              } else {
                await onRestoreEvent(confirmAction.event);
              }
              setConfirmAction(null);
            }}
            onCancel={() => setConfirmAction(null)}
          />
        )}
      </div>
    </div>
  );
}
